import {
  formatLiveClockDisplay,
  resolveLiveClockState,
  type LiveClockState,
} from './live-clock';

export interface StatPair {
  home: string;
  away: string;
}

export interface EventRowLiveInfo {
  homeScore: string;
  awayScore: string;
  /** Dimmed game / period scores per side, e.g. tennis games `6 4` or basketball quarters. */
  homeGameScores?: string;
  awayGameScores?: string;
  /** Dimmed match/set score per side when points are shown as the main score. */
  homeSetScore?: string;
  awaySetScore?: string;
  /** `0` = home serving, `1` = away serving. */
  currentServer?: 0 | 1;
  clock?: string;
  liveClockState?: LiveClockState;
}

export interface LiveStatistics {
  corners?: StatPair;
  yellowCards?: StatPair;
  redCards?: StatPair;
  penalties?: StatPair;
}

/** Partial statistics pushed over the live feed (socket). */
export interface LiveEventStatisticsPatch {
  currentPart?: string;
  currentPartShort?: string;
  currentTime?: string;
  currentTimeExtended?: string;
  score?: { home?: string | number; away?: string | number };
  currentPoint?: StatPair;
  /** `0` = home serving, `1` = away serving. */
  currentServer?: 0 | 1;
  setScores?: StatPair[];
  corners?: StatPair;
  yellowCards?: StatPair;
  redCards?: StatPair;
  penalties?: StatPair;
}

function toPascalCase(key: string): string {
  return key.charAt(0).toUpperCase() + key.slice(1);
}

function readTrimmedString(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value).trim();
}

function pickStatField(stats: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const value = readTrimmedString(stats[key]);
    if (value) return value;
  }
  return '';
}

function parseScorePair(value: unknown): StatPair | undefined {
  if (value === null || value === undefined) return undefined;

  if (typeof value === 'object' && value !== null) {
    const record = value as Record<string, unknown>;
    const home = record.Home ?? record.home;
    const away = record.Away ?? record.away;
    if (home !== undefined && away !== undefined) {
      return { home: String(home), away: String(away) };
    }
  }

  if (typeof value === 'string' && value.includes(':')) {
    const [home, away] = value.split(':');
    if (home !== undefined && away !== undefined) {
      return { home: home.trim(), away: away.trim() };
    }
  }

  return undefined;
}

/** Parse the raw `Statistics` JSON payload attached to live event rows. */
export function parseEventStatisticsRaw(
  raw: string | null | undefined,
): Record<string, unknown> | null {
  if (!raw?.trim()) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

function pickMatchTimeExtended(stats: Record<string, unknown>): string {
  return pickStatField(
    stats,
    'MatchTimeExtended',
    'matchTimeExtended',
    'MatchCurrentTimeExtended',
    'matchCurrentTimeExtended',
    'CurrentTimeExtended',
    'currentTimeExtended',
  );
}

function formatLiveScoreValue(value: unknown): string | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'number') {
    if (Number.isNaN(value)) return undefined;
    return Number.isInteger(value) ? String(value) : value.toFixed(1);
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) {
      return Number.isInteger(parsed) ? String(parsed) : parsed.toFixed(1);
    }
    return value.trim();
  }
  return undefined;
}

/** Merge a socket statistics patch into an event snapshot before resolving live info. */
export function applyLiveStatisticsPatch<
  T extends {
    HomeScore?: number | null;
    AwayScore?: number | null;
    Time?: string | null;
    Period?: string | null;
    PeriodShort?: string | null;
    Statistics?: string | null;
  },
>(event: T, patch?: LiveEventStatisticsPatch | null): T {
  if (!patch) return event;

  const stats = { ...(parseEventStatisticsRaw(event.Statistics) ?? {}) };

  if (patch.currentPart) {
    stats.CurrentPart = patch.currentPart;
    stats.currentPart = patch.currentPart;
  }
  if (patch.currentPartShort) {
    stats.CurrentPartShort = patch.currentPartShort;
    stats.currentPartShort = patch.currentPartShort;
  }
  if (patch.currentTime) {
    stats.CurrentTime = patch.currentTime;
    stats.currentTime = patch.currentTime;
    stats.MatchCurrentTime = patch.currentTime;
    stats.matchCurrentTime = patch.currentTime;
  }
  if (patch.currentTimeExtended) {
    stats.CurrentTimeExtended = patch.currentTimeExtended;
    stats.currentTimeExtended = patch.currentTimeExtended;
    stats.MatchCurrentTimeExtended = patch.currentTimeExtended;
    stats.matchCurrentTimeExtended = patch.currentTimeExtended;
    stats.MatchTimeExtended = patch.currentTimeExtended;
    stats.matchTimeExtended = patch.currentTimeExtended;
  }
  if (patch.score) {
    stats.Score = {
      Home: patch.score.home,
      Away: patch.score.away,
    };
    stats.score = `${patch.score.home}:${patch.score.away}`;
  }
  if (patch.corners) {
    stats.Corners = `${patch.corners.home}:${patch.corners.away}`;
  }
  if (patch.yellowCards) {
    stats.YellowCards = `${patch.yellowCards.home}:${patch.yellowCards.away}`;
  }
  if (patch.redCards) {
    stats.RedCards = `${patch.redCards.home}:${patch.redCards.away}`;
  }
  if (patch.penalties) {
    stats.Penalties = `${patch.penalties.home}:${patch.penalties.away}`;
  }
  if (patch.currentPoint) {
    stats.CurrentPoint = {
      Home: patch.currentPoint.home,
      Away: patch.currentPoint.away,
    };
    stats.currentPoint = `${patch.currentPoint.home}:${patch.currentPoint.away}`;
  }
  if (patch.currentServer !== undefined) {
    stats.CurrentServer = patch.currentServer;
    stats.currentServer = patch.currentServer;
  }
  if (patch.setScores?.length) {
    patch.setScores.forEach((setScore, index) => {
      const key = `Set${index + 1}Score`;
      stats[key] = { Home: setScore.home, Away: setScore.away };
      stats[`set${index + 1}Score`] = `${setScore.home}:${setScore.away}`;
    });
  }

  return {
    ...event,
    Statistics: JSON.stringify(stats),
    Time: patch.currentTime ?? event.Time,
    PeriodShort: patch.currentPartShort ?? event.PeriodShort,
    Period: patch.currentPart ?? event.Period,
    HomeScore:
      patch.score?.home !== undefined
        ? Number(patch.score.home)
        : event.HomeScore,
    AwayScore:
      patch.score?.away !== undefined
        ? Number(patch.score.away)
        : event.AwayScore,
  };
}

/**
 * Resolve live score and clock for event list rows. The API often keeps the
 * authoritative values inside `Statistics` (`Score` / `score`, `CurrentTime`,
 * `CurrentPartShort`, `matchTimeExtended`) rather than on the top-level
 * event fields.
 *
 * For tennis-like sports with `CurrentPoint` + set games:
 * - main score = current points
 * - game scores = Set1…SetN games for that side
 * - set score = overall match/set score
 */
export function resolveEventRowLiveInfo(
  event: {
    HomeScore?: number | null;
    AwayScore?: number | null;
    Time?: string | null;
    Period?: string | null;
    PeriodShort?: string | null;
    PeriodScore?: string | null;
    Statistics?: string | null;
  },
  translatePeriod?: (key: string) => string,
  anchoredAt?: number,
): EventRowLiveInfo {
  const stats = parseEventStatisticsRaw(event.Statistics);
  const matchScore = stats ? parseScorePair(stats.Score ?? stats.score) : undefined;
  const currentPoint = stats
    ? parseScorePair(stats.CurrentPoint ?? stats.currentPoint)
    : undefined;
  const setGames = stats ? collectSetGameScores(stats) : { home: [], away: [] };

  const matchHome =
    matchScore?.home ??
    formatLiveScoreValue(event.HomeScore) ??
    '0';
  const matchAway =
    matchScore?.away ??
    formatLiveScoreValue(event.AwayScore) ??
    '0';

  const homeGameScores =
    setGames.home.length > 0
      ? setGames.home.join(' ')
      : splitPeriodScores(event.PeriodScore)[0];
  const awayGameScores =
    setGames.away.length > 0
      ? setGames.away.join(' ')
      : splitPeriodScores(event.PeriodScore)[1];

  const usesPointScoring = Boolean(currentPoint);
  const currentServer = stats
    ? parseCurrentServer(stats.CurrentServer ?? stats.currentServer)
    : undefined;

  const homeScore = usesPointScoring
    ? (formatLiveScoreValue(currentPoint?.home) ?? '0')
    : matchHome;
  const awayScore = usesPointScoring
    ? (formatLiveScoreValue(currentPoint?.away) ?? '0')
    : matchAway;

  const currentPart = stats
    ? pickStatField(stats, 'CurrentPart', 'currentPart')
    : '';
  const period =
    (stats
      ? pickStatField(stats, 'CurrentPartShort', 'currentPartShort', 'CurrentPart', 'currentPart')
      : '') ||
    readTrimmedString(event.PeriodShort) ||
    readTrimmedString(event.Period);
  const time =
    (stats
      ? pickStatField(
          stats,
          'CurrentTime',
          'currentTime',
          'MatchCurrentTime',
          'matchCurrentTime',
        )
      : '') || readTrimmedString(event.Time);
  const timeExtended = stats ? pickMatchTimeExtended(stats) : '';

  const liveClockState = resolveLiveClockState({
    period,
    currentPart,
    time,
    timeExtended,
    anchoredAt,
  });

  return {
    homeScore,
    awayScore,
    homeGameScores,
    awayGameScores,
    homeSetScore: usesPointScoring ? matchHome : undefined,
    awaySetScore: usesPointScoring ? matchAway : undefined,
    currentServer,
    liveClockState,
    clock: liveClockState
      ? formatLiveClockDisplay(liveClockState, anchoredAt ?? Date.now(), translatePeriod)
      : undefined,
  };
}

function parseCurrentServer(value: unknown): 0 | 1 | undefined {
  if (value === null || value === undefined || value === '') return undefined;
  const numeric = typeof value === 'number' ? value : Number(String(value).trim());
  if (numeric === 0 || numeric === 1) return numeric;
  return undefined;
}

function collectSetGameScores(stats: Record<string, unknown>): {
  home: string[];
  away: string[];
} {
  const home: string[] = [];
  const away: string[] = [];

  for (let index = 1; index <= 7; index += 1) {
    const pair =
      parseScorePair(stats[`Set${index}Score`]) ??
      parseScorePair(stats[`set${index}Score`]) ??
      parseScorePair(stats[`Set${index}`]) ??
      parseScorePair(stats[`set${index}`]);
    if (!pair) continue;
    if (pair.home === '' && pair.away === '') continue;
    home.push(pair.home);
    away.push(pair.away);
  }

  return { home, away };
}

function splitPeriodScores(periodScore: string | null | undefined): [string?, string?] {
  if (!periodScore?.trim()) return [];
  const parts = periodScore.split('|').map((part) => part.trim());
  if (parts.length >= 2) return [parts[0] || undefined, parts[1] || undefined];
  return [];
}

function normalizeStatisticsRecord(
  record: Record<string, unknown>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(record).map(([key, value]) => [toPascalCase(key), value]),
  );
}

export function formatStatPair(value: StatPair | undefined): StatPair {
  return {
    home: value?.home?.trim() ? value.home : '-',
    away: value?.away?.trim() ? value.away : '-',
  };
}

/** Parse the `Statistics` JSON string from a live event details response. */
export function parseLiveStatistics(raw: string | null | undefined): LiveStatistics | null {
  if (!raw?.trim()) return null;

  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const stats = normalizeStatisticsRecord(parsed);

    return {
      corners: parseScorePair(stats.Corners),
      yellowCards: parseScorePair(stats.YellowCards),
      redCards: parseScorePair(stats.RedCards),
      penalties: parseScorePair(stats.Penalties),
    };
  } catch {
    return null;
  }
}

export type { LiveClockState };
